import { motion } from "framer-motion";
import { Placeholder } from "./Placeholder";
import { RealMedia } from "./RealMedia";

export const reveal = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const BlockLabel = ({ num, title }) => (
  <div className="flex items-baseline gap-3 mb-4 md:mb-6">
    <span className="text-xs font-bold text-[#FF5E3A] tracking-[0.2em]">{num}</span>
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1C1C1C]/50">{title}</span>
  </div>
);

const Caption = ({ text }) =>
  text ? <p className="mt-4 md:mt-6 text-sm md:text-base text-[#1C1C1C]/45 max-w-xl leading-relaxed">{text}</p> : null;

export const MediaItem = ({ item, projectMap, onOpen }) => {
  if (item.projectId) {
    const project = projectMap[item.projectId];
    if (project) return <RealMedia project={project} onOpen={() => onOpen(project)} />;
    return <Placeholder ph={item.ph || { kind: "video", format: "MEDIA", dims: "—", aspect: "16:9" }} />;
  }
  return <Placeholder ph={item.ph} />;
};

export const CaseBlock = ({ block, num, projectMap, onOpen }) => {
  if (block.type === "text") {
    return (
      <motion.div {...reveal} className="cs-block max-w-4xl">
        <p className="text-xl md:text-3xl leading-snug font-medium text-[#1C1C1C]/80">{block.body}</p>
      </motion.div>
    );
  }

  if (block.type === "full") {
    return (
      <motion.div {...reveal} className="cs-block">
        <BlockLabel num={num} title={block.title} />
        <MediaItem item={block.media} projectMap={projectMap} onOpen={onOpen} />
        <Caption text={block.caption} />
      </motion.div>
    );
  }

  if (block.type === "grid") {
    const colsClass = block.cols === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 md:grid-cols-2";
    return (
      <motion.div {...reveal} className="cs-block">
        <BlockLabel num={num} title={block.title} />
        <div className={`grid ${colsClass} gap-4 md:gap-6`}>
          {block.items.map((item, i) => (
            <MediaItem key={i} item={item} projectMap={projectMap} onOpen={onOpen} />
          ))}
        </div>
        <Caption text={block.caption} />
      </motion.div>
    );
  }

  if (block.type === "offset") {
    return (
      <motion.div {...reveal} className="cs-block">
        <BlockLabel num={num} title={block.title} />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start">
          <div className="md:col-span-8">
            <MediaItem item={block.items[0]} projectMap={projectMap} onOpen={onOpen} />
          </div>
          <div className="md:col-span-4 md:mt-28">
            <MediaItem item={block.items[1]} projectMap={projectMap} onOpen={onOpen} />
          </div>
        </div>
        <Caption text={block.caption} />
      </motion.div>
    );
  }

  if (block.type === "media-text") {
    return (
      <motion.div {...reveal} className="cs-block">
        <BlockLabel num={num} title={block.title} />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <MediaItem item={block.media} projectMap={projectMap} onOpen={onOpen} />
          </div>
          <div className="md:col-span-5 pb-2">
            <h3 className="text-2xl md:text-4xl font-black tracking-tight text-[#1C1C1C] mb-4">{block.heading}</h3>
            <p className="text-base md:text-lg text-[#1C1C1C]/50 leading-relaxed">{block.body}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};
